/**
 * Widget and type resolver registrations.
 *
 * This file is imported by registry/index.js for side-effect registration.
 * Each entry maps a server-defined name to a resolver function that returns
 * the appropriate Vue component (or null to decline, falling through to the
 * next resolution phase).
 *
 * To add a new widget:
 *   1. Add a registerWidget() or registerType() call here
 *   2. The resolver receives { page, editable, isEditMode }
 *   3. Return tagged('path', () => import('path/to/component.vue')) for the matching variant
 *   4. Return null to decline (falls through to type registry)
 *
 * ─── editable vs isEditMode ───
 *
 * Most resolvers branch on `ctx.editable` (the default). This means readonly=true
 * shows the *view* variant component, which is the correct behavior for simple widgets.
 *
 * Four composite container widgets use `ctx.isEditMode` instead:
 *   Contributors, ImagingChannels, NumberRange, RequiredAnnotations
 *
 * These widgets nest child FieldComponents with hardcoded `:is-edit-mode="true"`.
 * They need the edit UI structure (add/remove rows, sub-field controls) even when
 * the field itself or its sub-fields are readonly — the container manages its own
 * editability internally via the `editable` prop it receives.
 *
 * Rule: use `ctx.editable` unless the widget is a composite container that nests
 * FieldComponents and needs the edit shell regardless of field-level readonly state.
 *
 * ─── Array handling ───
 *
 * FieldResolver always passes the full value. Widgets that render per-item
 * (e.g. ItemLink, ObjectWidget) use FieldIterator internally. Composite
 * widgets (e.g. Contributors, StockLocation) manage their own iteration.
 */
import { defineAsyncComponent } from 'vue'

import FieldSkeleton from '@/components/fields/FieldSkeleton.vue'

import { registerType } from './typeRegistry'
import { registerWidget } from './widgetRegistry'

/**
 * Wrap an async component factory with a loading skeleton and dev-mode path.
 *
 * Results are cached by path. Without the cache, every call to a resolver
 * (e.g. resolveType('string', ctx)) would call defineAsyncComponent() again
 * and return a *new object reference*. Vue's <component :is> treats a changed
 * reference as a different component and unmounts + remounts it — losing input
 * focus on every keystroke that triggers a resolvedComponent re-evaluation.
 */
const _taggedCache = new Map()
function tagged(path, factory) {
  if (_taggedCache.has(path)) return _taggedCache.get(path)
  const comp = defineAsyncComponent({
    loader: factory,
    loadingComponent: FieldSkeleton,
    delay: 0,
  })
  comp._debugPath = path
  _taggedCache.set(path, comp)
  return comp
}

// --- Widget: AutoIncrement ---
registerWidget('AutoIncrement', (ctx) => {
  // AutoIncrement only has an edit variant (no view/list).
  // In view mode, return null to fall through to type-based resolution
  // (which will render as a string view).
  if (!ctx.editable) return null
  return tagged(
    'widget/AutoIncrement/detail/edit',
    () =>
      import('@/components/fields/widget/AutoIncrement/detail/edit/index.vue'),
  )
})

// --- Widget: Number ---
registerWidget('Number', (ctx) => {
  if (ctx.editable) {
    return tagged(
      'type/number/detail/edit',
      () => import('@/components/fields/type/number/detail/edit/index.vue'),
    )
  }
  return tagged(
    'type/number/detail/view',
    () => import('@/components/fields/type/number/detail/view/index.vue'),
  )
})

// --- Widget: LinkedItem ---
// ItemLink handles object references, UUID links, and copyable IDs.
// List and detail view wrappers handle array iteration internally.
registerWidget(
  'LinkedItem',
  (ctx) => {
    if (ctx.editable) {
      return tagged(
        'widget/ItemLink/detail/edit',
        () =>
          import('@/components/fields/widget/ItemLink/detail/edit/index.vue'),
      )
    }
    if (ctx.page === 'list') {
      return tagged(
        'widget/ItemLink/list/view',
        () => import('@/components/fields/widget/ItemLink/list/view/index.vue'),
      )
    }
    return tagged(
      'widget/ItemLink/detail/view',
      () => import('@/components/fields/widget/ItemLink/detail/view/index.vue'),
    )
  },
  { copyField: 'id', copyLabel: 'ID' },
)

// --- Type: number ---
registerType('number', (ctx) => {
  if (ctx.editable) {
    return tagged(
      'type/number/detail/edit',
      () => import('@/components/fields/type/number/detail/edit/index.vue'),
    )
  }
  return tagged(
    'type/number/detail/view',
    () => import('@/components/fields/type/number/detail/view/index.vue'),
  )
})

// --- Type: boolean ---
registerType('boolean', (ctx) => {
  if (ctx.editable) {
    return tagged(
      'type/boolean/detail/edit',
      () => import('@/components/fields/type/boolean/detail/edit/index.vue'),
    )
  }
  return tagged(
    'type/boolean/detail/view',
    () => import('@/components/fields/type/boolean/detail/view/index.vue'),
  )
})

// --- Type: date ---
registerType('date', (ctx) => {
  if (ctx.editable) {
    return tagged(
      'type/date/detail/edit',
      () => import('@/components/fields/type/date/detail/edit/index.vue'),
    )
  }
  return tagged(
    'type/date/detail/view',
    () => import('@/components/fields/type/date/detail/view/index.vue'),
  )
})

// --- Type: datetime ---
registerType('datetime', (ctx) => {
  if (ctx.editable) {
    return tagged(
      'type/datetime/detail/edit',
      () => import('@/components/fields/type/datetime/detail/edit/index.vue'),
    )
  }
  return tagged(
    'type/datetime/detail/view',
    () => import('@/components/fields/type/datetime/detail/view/index.vue'),
  )
})

// --- Widget: TextArea ---
registerWidget('TextArea', (ctx) => {
  if (ctx.page === 'list') {
    return tagged(
      'widget/TextArea/list/view',
      () => import('@/components/fields/widget/TextArea/list/view/index.vue'),
    )
  }
  if (ctx.editable) {
    return tagged(
      'widget/TextArea/detail/edit',
      () => import('@/components/fields/widget/TextArea/detail/edit/index.vue'),
    )
  }
  return tagged(
    'widget/TextArea/detail/view',
    () => import('@/components/fields/widget/TextArea/detail/view/index.vue'),
  )
})

// --- Widget: LinkedUser ---
registerWidget('LinkedUser', (ctx) => {
  if (ctx.editable) return null
  return tagged(
    'widget/UserLink/detail/view',
    () => import('@/components/fields/widget/UserLink/detail/view/index.vue'),
  )
})

// --- Widget: ItemCount ---
registerWidget('ItemCount', () => {
  return tagged(
    'widget/ItemCount/list/view',
    () => import('@/components/fields/widget/ItemCount/list/view/index.vue'),
  )
})

// --- Widget: Status ---
registerWidget('Status', (ctx) => {
  if (ctx.editable) return null
  return tagged(
    'widget/StatusComponent/detail/view',
    () =>
      import('@/components/fields/widget/StatusComponent/detail/view/index.vue'),
  )
})

// --- Widget: Image ---
// FieldResolver handles empty values, so we always resolve here.
// Image is not multiple — the array IS the value (flex row of thumbnails).
registerWidget('Image', (ctx) => {
  if (ctx.editable) return null
  return tagged(
    'widget/Image/detail/view',
    () => import('@/components/fields/widget/Image/detail/view/index.vue'),
  )
})

// --- Widget: PubmedID ---
// PubmedID is not multiple — the array of IDs IS the value (inline tags).
registerWidget('PubmedID', (ctx) => {
  if (ctx.editable) {
    return tagged(
      'widget/PubmedIds/detail/edit',
      () =>
        import('@/components/fields/widget/PubmedIds/detail/edit/index.vue'),
    )
  }
  return tagged(
    'widget/PubmedIds/detail/view',
    () => import('@/components/fields/widget/PubmedIds/detail/view/index.vue'),
  )
})

// --- Widget: TagInput ---
registerWidget('TagInput', (ctx) => {
  if (ctx.editable) {
    return tagged(
      'widget/TagInput/detail/edit',
      () => import('@/components/fields/widget/TagInput/detail/edit/index.vue'),
    )
  }
  return tagged(
    'widget/TagInput/detail/view',
    () => import('@/components/fields/widget/TagInput/detail/view/index.vue'),
  )
})

// --- Widget: Barcode ---
// In view mode, return null to fall through to string type resolution.
registerWidget('Barcode', (ctx) => {
  if (!ctx.editable) return null
  return tagged(
    'widget/Barcode/detail/edit',
    () => import('@/components/fields/widget/Barcode/detail/edit/index.vue'),
  )
})

// --- Widget: DateTimeSince ---
registerWidget('DateTimeSince', (ctx) => {
  if (ctx.editable) return null
  return tagged(
    'widget/DateTimeSince/detail/view',
    () =>
      import('@/components/fields/widget/DateTimeSince/detail/view/index.vue'),
  )
})

// --- Widget: GalaxyAnnotation ---
registerWidget('GalaxyAnnotation', (ctx) => {
  if (ctx.editable) return null
  return tagged(
    'widget/GalaxyAnnotation/detail/view',
    () =>
      import('@/components/fields/widget/GalaxyAnnotation/detail/view/index.vue'),
  )
})

// --- Widget: ROCrateAnnotation ---
registerWidget('ROCrateAnnotation', (ctx) => {
  if (ctx.editable) return null
  return tagged(
    'widget/ROCrateAnnotation/detail/view',
    () =>
      import('@/components/fields/widget/ROCrateAnnotation/detail/view/index.vue'),
  )
})

// --- Widget: DMAppAnnotation ---
registerWidget('DMAppAnnotation', (ctx) => {
  if (ctx.editable) return null
  return tagged(
    'widget/DmappAnnotation/detail/view',
    () =>
      import('@/components/fields/widget/DmappAnnotation/detail/view/index.vue'),
  )
})

// --- Widget: Contributors ---
// Detail/view index.vue handles its own array iteration with DeletableItem per-item.
registerWidget('Contributors', (ctx) => {
  if (ctx.page === 'list') {
    return tagged(
      'widget/Contributors/list/view',
      () =>
        import('@/components/fields/widget/Contributors/list/view/index.vue'),
    )
  }
  // Use isEditMode (not editable) to match FieldComponent behavior:
  // Contributors edit shows add/remove UI even when readonly fields exist.
  if (ctx.isEditMode) {
    return tagged(
      'widget/Contributors/detail/edit',
      () =>
        import('@/components/fields/widget/Contributors/detail/edit/index.vue'),
    )
  }
  return tagged(
    'widget/Contributors/detail/view',
    () =>
      import('@/components/fields/widget/Contributors/detail/view/index.vue'),
  )
})

// --- Widget: StockLocation ---
// Composite value widget — manages its own array rendering (location hierarchy).
registerWidget('StockLocation', (ctx) => {
  if (ctx.page === 'list') {
    return tagged(
      'widget/StockLocation/list/view',
      () =>
        import('@/components/fields/widget/StockLocation/list/view/index.vue'),
    )
  }
  if (ctx.editable) {
    return tagged(
      'widget/StockLocation/detail/edit',
      () =>
        import('@/components/fields/widget/StockLocation/detail/edit/index.vue'),
    )
  }
  return tagged(
    'widget/StockLocation/detail/view',
    () =>
      import('@/components/fields/widget/StockLocation/detail/view/index.vue'),
  )
})

// --- Widget: Object ---
// List view uses modal for arrays; detail view expands inline.
registerWidget('Object', (ctx) => {
  if (ctx.editable) {
    return tagged(
      'widget/ObjectWidget/detail/edit',
      () =>
        import('@/components/fields/widget/ObjectWidget/detail/edit/index.vue'),
    )
  }
  if (ctx.page === 'list') {
    return tagged(
      'widget/ObjectWidget/list/view',
      () =>
        import('@/components/fields/widget/ObjectWidget/list/view/index.vue'),
    )
  }
  return tagged(
    'widget/ObjectWidget/detail/view',
    () =>
      import('@/components/fields/widget/ObjectWidget/detail/view/index.vue'),
  )
})

// --- Type: object ---
registerType('object', (ctx) => {
  if (ctx.editable) {
    return tagged(
      'widget/ObjectWidget/detail/edit',
      () =>
        import('@/components/fields/widget/ObjectWidget/detail/edit/index.vue'),
    )
  }
  if (ctx.page === 'list') {
    return tagged(
      'widget/ObjectWidget/list/view',
      () =>
        import('@/components/fields/widget/ObjectWidget/list/view/index.vue'),
    )
  }
  return tagged(
    'widget/ObjectWidget/detail/view',
    () =>
      import('@/components/fields/widget/ObjectWidget/detail/view/index.vue'),
  )
})

// --- Type: string (default fallback) ---
registerType('string', (ctx) => {
  if (ctx.editable) {
    return tagged(
      'type/string/detail/edit',
      () => import('@/components/fields/type/string/detail/edit/index.vue'),
    )
  }
  return tagged(
    'type/string/detail/view/StringView',
    () => import('@/components/fields/type/string/detail/view/StringView.vue'),
  )
})

// --- Widget: NumberRange ---
// Uses isEditMode (not editable): composite widget with min/max sub-fields that
// need the edit shell even when individual sub-fields may be readonly.
registerWidget('NumberRange', (ctx) => {
  if (ctx.isEditMode) {
    return tagged(
      'widget/NumberRange/detail/edit',
      () =>
        import('@/components/fields/widget/NumberRange/detail/edit/index.vue'),
    )
  }
  return tagged(
    'widget/NumberRange/detail/view',
    () =>
      import('@/components/fields/widget/NumberRange/detail/view/index.vue'),
  )
})

// --- Widget: Permissions ---
// Composite value widget — manages its own value structure.
registerWidget('Permissions', () => {
  return tagged(
    'widget/Permission/detail/view',
    () => import('@/components/fields/widget/Permission/detail/view/index.vue'),
  )
})

// --- Widget: NumberUnit ---
registerWidget('NumberUnit', (ctx) => {
  if (ctx.editable) {
    return tagged(
      'widget/NumberUnit/detail/edit',
      () =>
        import('@/components/fields/widget/NumberUnit/detail/edit/index.vue'),
    )
  }
  return tagged(
    'widget/NumberUnit/detail/view',
    () => import('@/components/fields/widget/NumberUnit/detail/view/index.vue'),
  )
})

// --- Widget: ProtocolList ---
// Composite value widget — manages its own value structure.
registerWidget('ProtocolList', (ctx) => {
  if (ctx.page === 'list') {
    return tagged(
      'widget/ProtocolList/list/view',
      () =>
        import('@/components/fields/widget/ProtocolList/list/view/index.vue'),
    )
  }
  if (ctx.editable) {
    return tagged(
      'widget/ProtocolList/detail/edit',
      () =>
        import('@/components/fields/widget/ProtocolList/detail/edit/index.vue'),
    )
  }
  return tagged(
    'widget/ProtocolList/detail/view',
    () =>
      import('@/components/fields/widget/ProtocolList/detail/view/index.vue'),
  )
})

// --- Widget: DefaultCategory ---
registerWidget('DefaultCategory', () => {
  return tagged(
    'widget/DefaultCategory/detail/edit',
    () =>
      import('@/components/fields/widget/DefaultCategory/detail/edit/index.vue'),
  )
})

// --- Widget: InstrumentModel ---
registerWidget('InstrumentModel', () => {
  return tagged(
    'widget/InstrumentModel/detail/edit',
    () =>
      import('@/components/fields/widget/InstrumentModel/detail/edit/index.vue'),
  )
})

// --- Widget: ImagingChannels ---
// Composite value widget — manages its own array rendering.
registerWidget('ImagingChannels', (ctx) => {
  if (ctx.page === 'list') {
    return tagged(
      'widget/ImagingChannel/list/view',
      () =>
        import('@/components/fields/widget/ImagingChannel/list/view/index.vue'),
    )
  }
  if (ctx.isEditMode) {
    return tagged(
      'widget/ImagingChannel/detail/edit',
      () =>
        import('@/components/fields/widget/ImagingChannel/detail/edit/index.vue'),
    )
  }
  return tagged(
    'widget/ImagingChannel/detail/view',
    () =>
      import('@/components/fields/widget/ImagingChannel/detail/view/index.vue'),
  )
})

// --- Widget: RequiredAnnotations ---
// Composite value widget — manages its own array rendering.
// Uses isEditMode (not editable): composite container that nests annotation
// sub-fields via FieldComponents with hardcoded is-edit-mode.
registerWidget('RequiredAnnotations', (ctx) => {
  if (ctx.isEditMode) {
    return tagged(
      'widget/RequiredAnnotations/detail/edit',
      () =>
        import('@/components/fields/widget/RequiredAnnotations/detail/edit/index.vue'),
    )
  }
  return tagged(
    'widget/RequiredAnnotations/detail/view',
    () =>
      import('@/components/fields/widget/RequiredAnnotations/detail/view/index.vue'),
  )
})

// --- Widget: Duration ---
registerWidget('Duration', (ctx) => {
  if (ctx.editable) return null
  return tagged(
    'widget/Duration/detail/view',
    () => import('@/components/fields/widget/Duration/detail/view/index.vue'),
  )
})

// --- Widget: LocationBreadcrumb ---
// Composite value widget — manages its own array rendering (breadcrumb trail).
registerWidget('LocationBreadcrumb', (ctx) => {
  if (ctx.editable) return null
  return tagged(
    'widget/LocationBreadcrumb/detail/view',
    () =>
      import('@/components/fields/widget/LocationBreadcrumb/detail/view/index.vue'),
  )
})

// --- Widget: DatafileCopyWidget ---
// Composite value widget — manages its own array rendering.
registerWidget('DatafileCopyWidget', (ctx) => {
  if (ctx.editable) return null
  if (ctx.page === 'list') {
    return tagged(
      'widget/DatafileCopy/list/view',
      () =>
        import('@/components/fields/widget/DatafileCopy/list/view/index.vue'),
    )
  }
  return tagged(
    'widget/DatafileCopy/detail/view',
    () =>
      import('@/components/fields/widget/DatafileCopy/detail/view/index.vue'),
  )
})

// --- Widget: RichText ---
registerWidget('RichText', (ctx) => {
  if (ctx.page === 'list') {
    return tagged(
      'widget/RichText/list/view',
      () => import('@/components/fields/widget/RichText/list/view/index.vue'),
    )
  }
  if (ctx.editable) {
    return tagged(
      'widget/RichText/detail/edit',
      () => import('@/components/fields/widget/RichText/detail/edit/index.vue'),
    )
  }
  return tagged(
    'widget/RichText/detail/view',
    () => import('@/components/fields/widget/RichText/detail/view/index.vue'),
  )
})

// --- Type: json ---
// Editable json is an edge case ("should not be possible" — server should mark readonly).
// We resolve to StringEdit so the user can at least input a value when empty.
registerType('json', (ctx) => {
  if (ctx.editable) {
    return tagged(
      'type/string/detail/edit',
      () => import('@/components/fields/type/string/detail/edit/index.vue'),
    )
  }
  if (ctx.page === 'list') {
    return tagged(
      'widget/JSON/list/view',
      () => import('@/components/fields/widget/JSON/list/view/index.vue'),
    )
  }
  return tagged(
    'widget/JSON/detail/view',
    () => import('@/components/fields/widget/JSON/detail/view/index.vue'),
  )
})

// --- Widget: OrderedItems ---
// Composite value widget — manages its own array rendering (ordered items list).
// Uses isEditMode (not editable): composite container that nests sub-field
// FieldComponents with hardcoded is-edit-mode.
registerWidget('OrderedItems', (ctx) => {
  if (ctx.isEditMode) {
    return tagged(
      'widget/OrderedItems/detail/edit',
      () =>
        import('@/components/fields/widget/OrderedItems/detail/edit/index.vue'),
    )
  }
  return tagged(
    'widget/OrderedItems/detail/view',
    () =>
      import('@/components/fields/widget/OrderedItems/detail/view/index.vue'),
  )
})

// --- Type: ordereditems ---
// TODO-SERVER-018: ordereditems should use widget.type='OrderedItems' instead of
// relying on a type-based fallback. Migrate server to always set the widget preference.
registerType('ordereditems', (ctx) => {
  if (ctx.isEditMode) {
    return tagged(
      'widget/OrderedItems/detail/edit',
      () =>
        import('@/components/fields/widget/OrderedItems/detail/edit/index.vue'),
    )
  }
  return tagged(
    'widget/OrderedItems/detail/view',
    () =>
      import('@/components/fields/widget/OrderedItems/detail/view/index.vue'),
  )
})

// --- Type: imagingchannels ---
// TODO-SERVER-012: imagingchannels should use widget.type='ImagingChannels' instead of
// relying on a type-based fallback. Migrate server to always set the widget preference.
registerType('imagingchannels', (ctx) => {
  if (ctx.editable) {
    return tagged(
      'widget/ImagingChannel/detail/edit',
      () =>
        import('@/components/fields/widget/ImagingChannel/detail/edit/index.vue'),
    )
  }
  return tagged(
    'widget/ImagingChannel/detail/view',
    () =>
      import('@/components/fields/widget/ImagingChannel/detail/view/index.vue'),
  )
})
